"use client";

import { useEffect, useRef, useState } from "react";
import type { FilterClause } from "../domain";
import { useTableLabelFn } from "./labels-context";

interface QuickFilterMenuProps {
  column: string;
  value: unknown;
  onApply: (clause: FilterClause) => void;
  onClose: () => void;
  x: number;
  y: number;
}

export function QuickFilterMenu({
  column,
  value,
  onApply,
  onClose,
  x,
  y,
}: QuickFilterMenuProps) {
  const t = useTableLabelFn();
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let nextX = x;
    let nextY = y;
    if (x + rect.width > window.innerWidth) {
      nextX = window.innerWidth - rect.width - 8;
    }
    if (y + rect.height > window.innerHeight) {
      nextY = window.innerHeight - rect.height - 8;
    }
    setPosition({ x: nextX, y: nextY });
  }, [x, y]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const sv = value === null || value === undefined ? "" : String(value);
  const isNum = typeof value === "number" || (!Number.isNaN(Number(sv)) && sv !== "");

  const applyText = () => {
    onApply({ column, type: "text", value: sv, operator: "equals" });
    onClose();
  };
  const applyContains = () => {
    onApply({ column, type: "text", value: sv, operator: "contains" });
    onClose();
  };
  const applyEmpty = () => {
    onApply({ column, type: "empty" });
    onClose();
  };
  const applyNotEmpty = () => {
    onApply({ column, type: "not-empty" });
    onClose();
  };
  const applyGt = () => {
    const n = Number(sv);
    if (Number.isFinite(n)) {
      onApply({ column, type: "number-range", min: n });
      onClose();
    }
  };
  const applyLt = () => {
    const n = Number(sv);
    if (Number.isFinite(n)) {
      onApply({ column, type: "number-range", max: n });
      onClose();
    }
  };

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={t("table.filters.quick.aria")}
      style={{ position: "fixed", left: position.x, top: position.y }}
      className="quickFilterMenu z-50 min-w-[180px] rounded-md border bg-background shadow-lg"
    >
      <div className="border-b px-3 py-1.5 text-xs font-medium text-text-secondary">
        {column}
      </div>
      <button
        role="menuitem"
        onClick={applyText}
        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
      >
        {t("quickFilterEquals", { value: sv })}
      </button>
      <button
        role="menuitem"
        onClick={applyContains}
        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
      >
        {t("quickFilterContains", { value: sv })}
      </button>
      {isNum && (
        <>
          <button
            role="menuitem"
            onClick={applyGt}
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
          >
            {t("quickFilterGreater", { value: sv })}
          </button>
          <button
            role="menuitem"
            onClick={applyLt}
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
          >
            {t("quickFilterLess", { value: sv })}
          </button>
        </>
      )}
      <div className="border-t" />
      <button
        role="menuitem"
        onClick={applyEmpty}
        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
      >
        {t("filterEmptyHint")}
      </button>
      <button
        role="menuitem"
        onClick={applyNotEmpty}
        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface"
      >
        {t("filterNonEmptyHint")}
      </button>
    </div>
  );
}
